import styled from 'styled-components';
import { Radio } from '@mui/material';
import COLORS from '../../colors';
import { ReactElement } from 'react';

const IconContainer = styled.div<RadioProps>`
  width: 32px;
  height: 32px;
  border-radius: 100px;
  color: ${(props) => (props.checked ? COLORS.ON_PRIMARY : COLORS.NEUTRAL_500)};
  background-color: ${(props) =>
    props.checked ? COLORS.PRIMARY_500 : COLORS.NEUTRAL_950};
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Icon = styled.div`
  width: 18px;
  height: 18px;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const Title = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
`;

const SupportingText = styled.div`
  align-self: stretch;
  position: relative;
  font-size: 11.11px;
  line-height: 12px;
  color: #94989e;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const HeadLineContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 7px 0px 0px;
  gap: 4px;
`;
const BasicButtonParent = styled.div<{ alignment: string }>`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: ${(props) =>
    props.alignment === 'center' ? 'center' : 'flex-start'};
  justify-content: center;
  gap: 8px;
`;
const ItemContainer = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const CardContainer = styled.div<RadioProps>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${(props) =>
    props.checked
      ? COLORS.CHECKED
      : props.backgroundColor || COLORS.NEUTRAL_925};
  padding: 12px 8px 12px 12px;
  border-radius: 12px;
  cursor: pointer;
`;

const DualIconContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 44px;
  height: 44px;
`;

const OverlapIconContainer = styled.div<RadioProps>`
  width: 32px;
  height: 32px;
  border-radius: 100px;
  color: ${(props) => (props.checked ? COLORS.ON_PRIMARY : COLORS.NEUTRAL_500)};
  background-color: ${(props) =>
    props.checked ? '#000c2a' : COLORS.NEUTRAL_950};

  background: ${(props) =>
    props.checked
      ? 'linear-gradient(35deg, rgba(0, 12, 42, 1) 0%, rgba(37, 66, 228, 1) 100%)'
      : ''};
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 18px;
  border: 2px solid;
  border-color: ${(props) => (props.checked ? COLORS.PRIMARY_500 : '#2A2B2F')};
  right: 0;
`;

interface RadioProps {
  checked: boolean;
  backgroundColor?: string;
}

interface CardProps {
  title: string;
  supportingText: string;
  icon: ReactElement | ReactElement[];
  checked: boolean;
  value: number;
  backgroundColor?: string;
  onChange: () => void;
  iconAlignment?: 'start' | 'center';
}

const Card = ({
  title,
  supportingText,
  icon,
  checked,
  value,
  backgroundColor,
  onChange,
  iconAlignment = 'start'
}: CardProps) => {
  return (
    <CardContainer
      onClick={onChange}
      checked={checked}
      backgroundColor={backgroundColor}
    >
      <ItemContainer>
        <BasicButtonParent alignment={iconAlignment}>
          {Array.isArray(icon) && icon.length > 1 && (
            <DualIconContainer>
              <IconContainer checked={checked}>
                <Icon>{icon[0]}</Icon>
              </IconContainer>
              <OverlapIconContainer checked={checked}>
                <Icon>{icon[1]}</Icon>
              </OverlapIconContainer>
            </DualIconContainer>
          )}

          {!Array.isArray(icon) && (
            <IconContainer checked={checked}>
              <Icon>{icon}</Icon>
            </IconContainer>
          )}
          <HeadLineContainer>
            <Title>{title}</Title>
            <SupportingText>{supportingText}</SupportingText>
          </HeadLineContainer>
        </BasicButtonParent>
        <Radio
          checked={checked}
          value={value}
          sx={{ color: COLORS.NEUTRAL_500 }}
        />
      </ItemContainer>
    </CardContainer>
  );
};

export default Card;
